import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Sparkles, Quote } from "lucide-react";
import { useDailyWish } from "@/hooks/useDailyWish";
import { FestiveParticles } from "@/components/site/FestiveLogo";
import { WishShare } from "@/components/site/WishShare";

const INTERVAL = 6500;

export const WishCarousel = ({ lang = "en" }) => {
  const wish = useDailyWish();
  const hi = lang === "hi";
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const slides = (hi && wish?.slides_hi?.length ? wish.slides_hi : wish?.slides) || [];
  useEffect(() => { if (i >= slides.length) setI(0); }, [slides.length, i]);
  useEffect(() => {
    if (paused || slides.length < 2) return;
    const t = setInterval(() => setI((n) => (n + 1) % slides.length), INTERVAL);
    return () => clearInterval(t);
  }, [paused, slides.length]);
  if (!wish || !slides.length) return null;
  const festive = wish.kind === "festival";
  const s = slides[i] || slides[0];
  return (
    <section className="relative overflow-hidden border-y border-white/10 bg-gf-surface" data-testid="wish-carousel" lang={hi ? "hi" : undefined}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} aria-label="Wish of the day">
      {wish.image_url && (
        <img src={wish.image_url} alt={`${wish.festival_name || wish.title || "Festive"} greetings from Growthifye`}
          className="absolute inset-0 w-full h-full object-cover opacity-45 scale-105" loading="eager" fetchpriority="high" />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-gf-bg via-gf-bg/80 to-gf-bg/30" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-gf-bg via-transparent to-transparent" aria-hidden="true" />
      {festive && <FestiveParticles effect={wish.effect} className="opacity-70 [&>span]:scale-[2.2]" />}
      <div className="relative max-w-[1600px] mx-auto px-5 sm:px-8 py-8 sm:py-10 lg:py-12 grid lg:grid-cols-[1fr_auto] gap-6 items-end">
        <div className="min-w-0">
          <p className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gf-primary" data-testid="wish-carousel-kicker">
            {festive ? <Sparkles className="w-3.5 h-3.5" /> : <Quote className="w-3.5 h-3.5" />}
            {festive ? `${wish.is_eve ? (hi ? "कल · " : "Tomorrow · ") : ""}${wish.festival_name}` : (hi ? "आज का विचार" : "Thought for the day")} · {hi ? "विदुर द्वारा" : "by Vidura"}
          </p>
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.45 }}
              className="mt-3" data-testid={`wish-slide-${i}`}>
              <h2 className="font-heading font-black text-gf-text text-2xl sm:text-3xl lg:text-4xl leading-[1.05] max-w-4xl" data-testid="wish-slide-headline">{s.headline}</h2>
              <p className="font-body text-sm sm:text-base text-gf-text/85 mt-3 max-w-3xl leading-relaxed" data-testid="wish-slide-body">{s.body}</p>
            </motion.div>
          </AnimatePresence>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1">
            {festive && <p className="font-body text-sm text-gf-primary" data-testid="wish-carousel-wish" lang={hi ? "hi" : "en"}>{hi && wish.wish_hi ? wish.wish_hi : wish.wish_en}</p>}
            {(hi ? festive && wish.wish_en : wish.wish_hi) && <p className="font-body text-sm text-gf-muted" lang={hi ? "en" : "hi"} data-testid="wish-carousel-hi">{hi ? wish.wish_en : wish.wish_hi}</p>}
          </div>
          {wish.tagline && <p className="font-mono text-[10px] uppercase tracking-wider text-gf-muted mt-2">{wish.tagline}</p>}
          <div className="mt-4"><WishShare wish={wish} lang={lang} /></div>
        </div>
        <div className="flex items-center gap-3 self-end lg:pb-1">
          <div className="flex items-center gap-1.5" data-testid="wish-carousel-dots">
            {slides.map((_, k) => (
              <button key={k} onClick={() => setI(k)} aria-label={`Slide ${k + 1}`} data-testid={`wish-dot-${k}`}
                className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${k === i ? "w-6 bg-gf-primary" : "w-1.5 bg-white/25 hover:bg-white/50"}`} />
            ))}
          </div>
          {slides.length > 1 && (
            <div className="flex gap-1">
              <button onClick={() => setI((i - 1 + slides.length) % slides.length)} aria-label="Previous" data-testid="wish-prev"
                className="w-8 h-8 border border-white/15 text-gf-muted hover:text-gf-primary hover:border-gf-primary/50 inline-flex items-center justify-center transition-colors"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setI((i + 1) % slides.length)} aria-label="Next" data-testid="wish-next"
                className="w-8 h-8 border border-white/15 text-gf-muted hover:text-gf-primary hover:border-gf-primary/50 inline-flex items-center justify-center transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
